// Pure loot generation + gear stat aggregation. No Pixi, no DOM. (TECHNICAL_DESIGN.md §2, §8, §9.)
import type { Rng } from './rng';
import type { Affix, AffixId, GearItem, LegendaryId, MotifId, Rarity, RelicEffectId, RelicItem, Slot, Stats } from './types';

export const SLOTS: Slot[] = ['weapon', 'subweapon', 'armor', 'necklace', 'ring', 'cloak'];
export const MOTIFS: MotifId[] = ['wolf', 'ember', 'root'];
export const MOTIF_NAMES: Record<MotifId, string> = { wolf: 'Wolf', ember: 'Ember', root: 'Root' };
const EMP_ATK = 2; // guaranteed weapon atk per empower level
const EMP_DEF = 1; // guaranteed armor def per empower level
export const EMPOWER_MAX = 12;
export const RARITY_COLORS = [0x9a9a9a, 0x5ac85a, 0x4a90e2, 0xb060e0, 0xf0a030]; // gray→orange
export const RARITY_NAMES = ['Common', 'Uncommon', 'Rare', 'Epic', 'Legendary'];
const BASE_WEIGHTS = [55, 25, 13, 5.5, 1.5];

export const LEGENDARIES: { id: LegendaryId; name: string; desc: string }[] = [
  { id: 'kill_heal', name: 'the Bloodroot', desc: 'Kills restore 2 HP.' },
  { id: 'gold_magnet', name: 'the Magpie', desc: 'Gold is worth +50%.' },
  { id: 'bounty', name: 'the Harvest', desc: '+1 yield from every resource node.' },
  { id: 'night_stride', name: 'the Nightwalker', desc: 'Move 12% faster at night.' },
  { id: 'chain_crit', name: 'the Chainlightning', desc: 'Crits arc to a second enemy.' },
];

const PREFIX = ['', 'Sturdy', 'Keen', 'Savage', 'Ancient'];
const NOUN: Record<Slot, string> = { weapon: 'Blade', subweapon: 'Bow', armor: 'Mail', necklace: 'Pendant', ring: 'Ring', cloak: 'Cloak' };
export const SLOT_ICON: Record<Slot, string> = { weapon: '⚔️', subweapon: '🏹', armor: '🛡️', necklace: '📿', ring: '💍', cloak: '🧥' };
export const SLOT_LABEL: Record<Slot, string> = { weapon: 'Weapon', subweapon: 'Sub-weapon', armor: 'Armor', necklace: 'Necklace', ring: 'Ring', cloak: 'Cloak' };
const ATK_SLOTS = new Set<Slot>(['weapon', 'subweapon']);
const DEF_SLOTS = new Set<Slot>(['armor', 'cloak']);
const AFFIX_SUFFIX: Record<AffixId, string> = {
  atk: 'of the Bear', hp: 'of the Ox', def: 'of the Turtle', spd: 'of the Hare', crit: 'of the Fox',
};

/** Floor-shifted rarity weights: deeper floors tilt toward the top tiers. */
function rarityWeights(floor: number): number[] {
  const shift = 1 + floor * 0.06;
  return BASE_WEIGHTS.map((w, r) => (r >= 2 ? w * shift : w));
}

function rollRarity(floor: number, rng: Rng): Rarity {
  const w = rarityWeights(floor);
  const total = w.reduce((a, b) => a + b, 0);
  let x = rng.float() * total;
  for (let r = 0; r < w.length; r++) { if (x < w[r]) return r as Rarity; x -= w[r]; }
  return 0;
}

const AFFIX_IDS: AffixId[] = ['atk', 'hp', 'def', 'spd', 'crit'];

function affixValue(id: AffixId, floor: number, rng: Rng): number {
  const f = 1 + floor;
  switch (id) {
    case 'atk': return 1 + rng.int(2 + floor);
    case 'hp': return 5 + rng.int(5 + floor * 3);
    case 'def': return 1 + rng.int(1 + floor);
    case 'spd': return 2 + rng.int(4);
    case 'crit': return 1 + rng.int(4);
    default: return f;
  }
}

/** Draw `rarity` distinct affixes; smart-loot weights the pool 2:1 toward equipped affixes. */
function rollAffixes(rarity: Rarity, floor: number, rng: Rng, equippedAffixIds: Set<AffixId>): Affix[] {
  const pool: AffixId[] = [];
  for (const id of AFFIX_IDS) { pool.push(id); if (equippedAffixIds.has(id)) pool.push(id); } // 2:1 bias
  const chosen: AffixId[] = [];
  const out: Affix[] = [];
  while (out.length < rarity && pool.length) {
    const id = pool[rng.int(pool.length)];
    if (chosen.includes(id)) { // avoid dupes but keep it terminating
      const remaining = AFFIX_IDS.filter((a) => !chosen.includes(a));
      if (!remaining.length) break;
      const alt = remaining[rng.int(remaining.length)];
      chosen.push(alt); out.push({ id: alt, value: affixValue(alt, floor, rng) });
      continue;
    }
    chosen.push(id); out.push({ id, value: affixValue(id, floor, rng) });
  }
  return out;
}

function baseStats(slot: Slot, floor: number): Partial<Stats> {
  if (ATK_SLOTS.has(slot)) return { atk: 4 + floor * 2 };
  if (DEF_SLOTS.has(slot)) return { def: 2 + floor };
  return {}; // necklace / ring: value comes entirely from affixes
}

function genName(slot: Slot, rarity: Rarity, affixes: Affix[], legendary?: LegendaryId): string {
  const prefix = PREFIX[rarity];
  const suffix = affixes.length ? ' ' + AFFIX_SUFFIX[affixes[0].id] : '';
  const legName = legendary ? ' ' + LEGENDARIES.find((l) => l.id === legendary)!.name : '';
  return `${prefix ? prefix + ' ' : ''}${NOUN[slot]}${suffix}${legName}`.trim();
}

export interface RollArgs {
  floor: number;
  rng: Rng;
  equipped: { atkIds: Set<AffixId> }; // smart-loot bias source
  slot?: Slot;
  minRarity?: Rarity;
}

/** The single gear-creation entry point (uid is minted by game.ts after). */
export function rollGear(a: RollArgs): GearItem {
  const slot = a.slot ?? SLOTS[a.rng.int(SLOTS.length)];
  let rarity = rollRarity(a.floor, a.rng);
  if (a.minRarity != null && rarity < a.minRarity) rarity = a.minRarity;
  const affixes = rollAffixes(rarity, a.floor, a.rng, a.equipped.atkIds);
  const legendary = rarity >= 3 ? LEGENDARIES[a.rng.int(LEGENDARIES.length)].id : undefined;
  const motif = a.rng.float() < 0.35 ? MOTIFS[a.rng.int(MOTIFS.length)] : undefined;
  return {
    uid: '', slot, rarity, floor: a.floor,
    base: baseStats(slot, a.floor), affixes, legendary, motif,
    empower: { level: 0, bumps: {} },
    name: genName(slot, rarity, affixes, legendary),
  };
}

// ---- empower (tamed upgrade-reveals) ----
export function empowerCost(level: number): number {
  return Math.round(50 * Math.pow(1.6, level)); // deterministic, rising, no fail chance
}
/** Base stats including the guaranteed per-level growth (weapon atk / armor def only). */
export function empoweredBase(g: GearItem): Partial<Stats> {
  const b = { ...g.base };
  if (ATK_SLOTS.has(g.slot)) b.atk = (b.atk ?? 0) + g.empower.level * EMP_ATK;
  if (DEF_SLOTS.has(g.slot)) b.def = (b.def ?? 0) + g.empower.level * EMP_DEF;
  return b;
}

export function getEquipped(gear: GearItem[], equipped: Record<string, string | undefined>, slot: Slot): GearItem | undefined {
  const uid = equipped[slot];
  return uid ? gear.find((g) => g.uid === uid) : undefined;
}

function foldAffix(t: Stats, id: AffixId, v: number): void {
  if (id === 'atk') t.atk += v;
  else if (id === 'hp') t.maxHp += v;
  else if (id === 'def') t.def += v;
  else if (id === 'spd') t.spdPct += v;
  else if (id === 'crit') t.critPct += v;
}

/** Sum of ALL affixes + empower bumps across the equipped slots (weapon base.atk / armor base.def
 *  are handled separately as the primary; trinket has no base). */
export function equippedAffixSum(gear: GearItem[], equipped: Record<string, string | undefined>): Stats {
  const t: Stats = { atk: 0, maxHp: 0, def: 0, spdPct: 0, critPct: 0 };
  for (const slot of SLOTS) {
    const g = getEquipped(gear, equipped, slot);
    if (!g) continue;
    for (const a of g.affixes) foldAffix(t, a.id, a.value);
    for (const [id, v] of Object.entries(g.empower.bumps)) foldAffix(t, id as AffixId, v ?? 0);
  }
  return t;
}

/** Gradient motif set bonuses — stacking per equipped piece, no thresholds, off-motif never hurts. */
export function motifEffects(gear: GearItem[], equipped: Record<string, string | undefined>): { lifestealPct: number; emberPct: number; hpFlat: number } {
  let wolf = 0, ember = 0, root = 0;
  for (const slot of SLOTS) {
    const m = getEquipped(gear, equipped, slot)?.motif;
    if (m === 'wolf') wolf++; else if (m === 'ember') ember++; else if (m === 'root') root++;
  }
  return { lifestealPct: wolf * 4, emberPct: ember * 15, hpFlat: root * 12 };
}

/** Set of affix ids on equipped gear — smart-loot bias source. */
export function equippedAffixIds(gear: GearItem[], equipped: Record<string, string | undefined>): Set<AffixId> {
  const s = new Set<AffixId>();
  for (const slot of SLOTS) getEquipped(gear, equipped, slot)?.affixes.forEach((a) => s.add(a.id));
  return s;
}

export function salvageValue(g: GearItem): number {
  return 3 + g.rarity * 6 + g.floor * 2 + g.empower.level * 4;
}

// ---- relics: dungeon-only passives, two worn at once ----
export interface RelicPower { regenPerS: number; xpPct: number; dodgePct: number; spdPct: number; goldPct: number }
export const RELIC_EFFECTS: { id: RelicEffectId; name: string; icon: string; desc: string }[] = [
  { id: 'regen', name: 'Verdant Heart', icon: '💚', desc: 'Slowly regenerate health.' },
  { id: 'xp', name: 'Sage Stone', icon: '📘', desc: 'Gain more XP from everything.' },
  { id: 'dodge', name: 'Wind Charm', icon: '🍃', desc: 'Chance to dodge incoming hits.' },
  { id: 'momentum', name: 'Swift Idol', icon: '👟', desc: 'Move faster, always.' },
  { id: 'fortune', name: 'Gilded Acorn', icon: '🌰', desc: 'Find more gold.' },
];
export const RELIC_ICON: Record<RelicEffectId, string> = Object.fromEntries(RELIC_EFFECTS.map((e) => [e.id, e.icon])) as Record<RelicEffectId, string>;

/** A single relic's contribution — magnitude grows with rarity (0..4 → ×1..×5). */
export function relicPower(r: RelicItem): Partial<RelicPower> {
  const s = 1 + r.rarity;
  switch (r.effect) {
    case 'regen': return { regenPerS: 0.5 * s };
    case 'xp': return { xpPct: 5 * s };
    case 'dodge': return { dodgePct: 3 * s };
    case 'momentum': return { spdPct: 3 * s };
    case 'fortune': return { goldPct: 8 * s };
  }
}

/** Sum the (≤2) equipped relics into one effect bundle. */
export function relicEffects(relics: RelicItem[], equippedRelics: (string | null)[]): RelicPower {
  const t: RelicPower = { regenPerS: 0, xpPct: 0, dodgePct: 0, spdPct: 0, goldPct: 0 };
  for (const uid of equippedRelics) {
    if (!uid) continue;
    const r = relics.find((x) => x.uid === uid);
    if (!r) continue;
    const p = relicPower(r);
    t.regenPerS += p.regenPerS ?? 0; t.xpPct += p.xpPct ?? 0; t.dodgePct += p.dodgePct ?? 0;
    t.spdPct += p.spdPct ?? 0; t.goldPct += p.goldPct ?? 0;
  }
  return t;
}

export function relicName(effect: RelicEffectId, rarity: Rarity): string {
  const base = RELIC_EFFECTS.find((e) => e.id === effect)!.name;
  return rarity >= 1 ? `${RARITY_NAMES[rarity]} ${base}` : base;
}

/** Roll a dungeon relic (uid minted by game.ts after). */
export function rollRelic(floor: number, rng: Rng): RelicItem {
  const rarity = rollRarity(floor, rng);
  const effect = RELIC_EFFECTS[rng.int(RELIC_EFFECTS.length)].id;
  return { uid: '', effect, rarity, floor, name: relicName(effect, rarity) };
}
